import type {BadgeVariant} from "@/components/BaseBadge";
import {BaseBadge} from "@/components/BaseBadge";
import {BaseCard} from "@/components/BaseCard";
import {PATHS} from "@/constants/routes";
import {useCountriesStats} from "@/hooks/useCountries";
import {useTransactions, useTransactionsStats} from "@/hooks/useTransactions";
import {useUsersStats} from "@/hooks/useUsers";
import {useWalletStats} from "@/hooks/useWallets";
import type {TransactionStatus, TransactionType} from "@/types/transaction.types";
import {ArrowLeftRight, ArrowRight, Clock, DollarSign, Globe, Users, Wallet} from "lucide-react";
import {useNavigate} from "react-router";
import {StatsCard} from "./components/StatsCard";

const TYPE_LABELS: Record<TransactionType, string> = {
  deposit: "Deposit",
  p2p_transfer: "P2P Transfer",
  remittance: "Remittance",
  withdrawal: "Withdrawal",
};

const STATUS_BADGE: Record<TransactionStatus, {variant: BadgeVariant; label: string}> = {
  pending: {variant: "neutral", label: "Pending"},
  processing: {variant: "warning", label: "Processing"},
  completed: {variant: "success", label: "Completed"},
  failed: {variant: "danger", label: "Failed"},
  refunded: {variant: "info", label: "Refunded"},
  cancelled: {variant: "neutral", label: "Cancelled"},
};

const formatUsd = (n: number) =>
  new Intl.NumberFormat("en-US", {style: "currency", currency: "USD", maximumFractionDigits: 0}).format(n);

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat("en-US", {month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"}).format(
    new Date(iso)
  );

export const Home = () => {
  const navigate = useNavigate();
  const {data: txStats} = useTransactionsStats();
  const {data: usersStats} = useUsersStats();
  const {data: walletStats} = useWalletStats();
  const {data: recentTx} = useTransactions({page: 1, limit: 5});
  const countriesStats = useCountriesStats();

  const recentTransactions = recentTx?.items ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatsCard
          label="Total Transactions"
          value={txStats?.totalTransactions?.toLocaleString() ?? "—"}
          icon={<ArrowLeftRight size={20} />}
          iconColor="var(--color-primary)"
        />
        <StatsCard
          label="Total Volume"
          value={txStats ? formatUsd(txStats.totalVolumeUsd / 100) : "—"}
          icon={<DollarSign size={20} />}
          iconColor="var(--color-success)"
        />
        <StatsCard
          label="Active Users"
          value={usersStats?.activeUsers?.toLocaleString() ?? "—"}
          icon={<Users size={20} />}
          iconColor="var(--color-accent)"
        />
        <StatsCard
          label="Países"
          value={
            countriesStats.active !== undefined && countriesStats.total !== undefined
              ? `${countriesStats.active}/${countriesStats.total}`
              : "—"
          }
          icon={<Globe size={20} />}
          iconColor="var(--color-accent)"
        />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatsCard
          label="Total Users"
          value={usersStats?.totalUsers?.toLocaleString() ?? "—"}
          icon={<Users size={20} />}
          iconColor="var(--color-primary)"
        />
        <StatsCard
          label="KYC Pending"
          value={usersStats?.kycPending?.toLocaleString() ?? "—"}
          icon={<Clock size={20} />}
          iconColor="var(--color-warning)"
        />
        <StatsCard
          label="Total Wallets"
          value={walletStats?.totalWallets?.toLocaleString() ?? "—"}
          icon={<Wallet size={20} />}
          iconColor="var(--color-accent)"
        />
        <StatsCard
          label="Pending Tx"
          value={txStats?.pending?.toLocaleString() ?? "—"}
          icon={<Clock size={20} />}
          iconColor="var(--color-info)"
        />
      </div>

      {/* Transaction type breakdown */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <BaseCard padding="md">
          <h2
            className="text-base font-semibold mb-4"
            style={{color: "var(--color-text)"}}
          >
            By Type
          </h2>
          <div className="flex flex-col gap-3">
            {(
              [
                {type: "remittance" as const, pct: 52, color: "var(--color-primary)"},
                {type: "deposit" as const, pct: 28, color: "var(--color-accent)"},
                {type: "p2p_transfer" as const, pct: 13, color: "var(--color-secondary)"},
                {type: "withdrawal" as const, pct: 7, color: "var(--color-warning)"},
              ] as const
            ).map(item => (
              <div
                key={item.type}
                className="flex flex-col gap-1"
              >
                <div className="flex items-center justify-between">
                  <span
                    className="text-xs"
                    style={{color: "var(--color-text-secondary)"}}
                  >
                    {TYPE_LABELS[item.type]}
                  </span>
                  <span
                    className="text-xs font-semibold"
                    style={{color: "var(--color-text)"}}
                  >
                    {item.pct}%
                  </span>
                </div>
                <div
                  className="w-full h-2 rounded-full"
                  style={{backgroundColor: "var(--color-surface-2)"}}
                >
                  <div
                    className="h-2 rounded-full"
                    style={{width: `${item.pct}%`, backgroundColor: item.color}}
                  />
                </div>
              </div>
            ))}
          </div>
        </BaseCard>
      </div>

      {/* Recent Transactions */}
      <BaseCard padding="none">
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{borderColor: "var(--color-border)"}}
        >
          <h2
            className="text-base font-semibold"
            style={{color: "var(--color-text)"}}
          >
            Recent Transactions
          </h2>
          <button
            type="button"
            onClick={() => navigate(PATHS.TRANSACTIONS)}
            className="text-xs font-medium transition-colors"
            style={{color: "var(--color-accent)"}}
          >
            View all{" "}
            <ArrowRight
              size={14}
              className="inline"
            />
          </button>
        </div>

        <div className="divide-y">
          {recentTransactions.map(tx => {
            const badge = STATUS_BADGE[tx.status];
            return (
              <div
                key={tx.id}
                className="flex items-center gap-4 px-6 py-3.5 hover:bg-(--color-surface-2) transition-colors cursor-pointer"
                onClick={() => navigate(PATHS.TRANSACTIONS)}
                onKeyDown={e => e.key === "Enter" && navigate(PATHS.TRANSACTIONS)}
                role="button"
                tabIndex={0}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-mono text-(--color-text-secondary)">{tx.id}</span>
                    {tx.corridor && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded"
                        style={{backgroundColor: "var(--color-surface-2)", color: "var(--color-text-secondary)"}}
                      >
                        {tx.corridor}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-(--color-text) truncate">{tx.userEmail}</p>
                  <p className="text-xs text-(--color-text-tertiary)">{TYPE_LABELS[tx.type]}</p>
                </div>

                <div className="text-right shrink-0">
                  <p
                    className="text-sm font-semibold"
                    style={{color: "var(--color-text)"}}
                  >
                    ${tx.amount.toLocaleString()}
                  </p>
                  <p
                    className="text-xs"
                    style={{color: "var(--color-text-tertiary)"}}
                  >
                    {formatDate(tx.createdAt)}
                  </p>
                </div>

                <div className="shrink-0 w-24 text-right">
                  <BaseBadge
                    variant={badge.variant}
                    label={badge.label}
                    dot
                  />
                </div>
              </div>
            );
          })}
        </div>
      </BaseCard>
    </div>
  );
};
